import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CalendarBlank,
  FunnelSimple,
  Clock,
  Plus
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import QuickUpdateModal from '@/components/QuickUpdateModal';

const API = `${window.location.origin}/api`;

const ACTION_LABELS = {
  task_created: 'Created',
  note_added: 'Note',
  status_changed: 'Status',
  task_completed: 'Completed',
  task_reopened: 'Reopened',
  priority_changed: 'Priority',
  due_date_changed: 'Date',
  project_changed: 'Project',
  manual_update: 'Updated',
  tags_changed: 'Tags',
  subtask_added: 'Subtask',
  subtask_completed: 'Subtask done',
  subtask_reopened: 'Subtask reopened',
  subtask_updated: 'Subtask edit',
  subtask_removed: 'Subtask removed',
};

const ACTION_COLORS = {
  task_created: 'text-navy',
  note_added: 'text-purple-600',
  status_changed: 'text-navy',
  task_completed: 'text-emerald-600',
  task_reopened: 'text-amber-600',
  priority_changed: 'text-orange-600',
  due_date_changed: 'text-green-600',
  project_changed: 'text-indigo-600',
  manual_update: 'text-slate-600',
  subtask_added: 'text-cyan-600',
  subtask_completed: 'text-emerald-600',
  subtask_reopened: 'text-amber-600',
  subtask_updated: 'text-slate-600',
  subtask_removed: 'text-red-600',
};

function WeekView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [quickUpdateTask, setQuickUpdateTask] = useState(null);

  const fetchWeek = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProject) params.project_client = filterProject;
      if (filterAction) params.action_type = filterAction;
      const [weekRes, projRes] = await Promise.all([
        axios.get(`${API}/activity/week`, { params, withCredentials: true }),
        axios.get(`${API}/tasks/projects`, { withCredentials: true })
      ]);
      setData(weekRes.data);
      setProjects(projRes.data);
    } catch (error) {
      console.error('Failed to fetch week activity:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWeek(); }, [filterProject, filterAction]);

  const formatDayHeader = (dateStr) => {
    try {
      const d = parseISO(dateStr);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return 'Today';
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return format(d, 'EEEE, MMM d');
    } catch { return dateStr; }
  };

  const formatTime = (ts) => {
    try { return format(parseISO(ts), 'h:mm a'); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="week-view">
      {/* Hint */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-5 flex items-center gap-2">
        <CalendarBlank size={16} weight="fill" className="text-navy flex-shrink-0" />
        <p className="text-sm text-blue-800">Use this before weekly status meetings or client calls.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <FunnelSimple size={16} />
          <span>Filter:</span>
        </div>
        <select
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700"
          data-testid="week-project-filter"
        >
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700"
          data-testid="week-action-filter"
        >
          <option value="">All Actions</option>
          <option value="task_created">New Tasks</option>
          <option value="note_added">Notes</option>
          <option value="status_changed">Status Changes</option>
          <option value="task_completed">Completions</option>
          <option value="priority_changed">Priority Changes</option>
          <option value="subtask_completed">Sub-task Completions</option>
          <option value="subtask_added">Sub-task Added</option>
        </select>
        <span className="ml-auto text-sm text-slate-500" data-testid="week-stats">
          {data?.total_entries || 0} entries &middot; {data?.total_tasks_touched || 0} tasks
        </span>
      </div>

      {/* Day Groups */}
      {(!data?.days || data.days.length === 0) ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="week-empty">
          <Clock size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No activity this week yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.days.map((day) => (
            <div key={day.date} data-testid={`week-day-${day.date}`}>
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                {formatDayHeader(day.date)}
                <span className="font-normal text-slate-400">({day.entries.length})</span>
              </h3>

              <div className="space-y-1.5 ml-4 border-l-2 border-slate-200 pl-4">
                {day.entries.map((entry, i) => (
                  <div
                    key={`${entry.task_id}-${i}`}
                    className="flex items-start gap-3 py-2 group"
                    data-testid={`week-entry-${entry.task_id}-${i}`}
                  >
                    <span className="text-xs text-slate-400 font-mono w-16 flex-shrink-0 pt-0.5">
                      {formatTime(entry.time)}
                    </span>
                    {entry.project_client && (
                      <span className="text-xs font-medium text-navy bg-blue-50 px-2 py-0.5 rounded flex-shrink-0">
                        {entry.project_client}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-900 font-medium">{entry.task_title}</span>
                      <span className="text-sm text-slate-400 mx-1.5">&ndash;</span>
                      <span className={`text-sm ${ACTION_COLORS[entry.action_type] || 'text-slate-600'}`}>
                        {entry.summary}
                      </span>
                    </div>
                    <button
                      onClick={() => setQuickUpdateTask({ task_id: entry.task_id, title: entry.task_title, project_client: entry.project_client })}
                      className="opacity-0 group-hover:opacity-100 text-xs text-navy bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-all flex items-center gap-1"
                      data-testid={`week-update-${entry.task_id}`}
                    >
                      <Plus size={10} weight="bold" /> Update
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {quickUpdateTask && (
        <QuickUpdateModal
          taskId={quickUpdateTask.task_id}
          taskTitle={quickUpdateTask.title}
          currentProject={quickUpdateTask.project_client}
          onClose={() => setQuickUpdateTask(null)}
          onComplete={() => { setQuickUpdateTask(null); fetchWeek(); }}
        />
      )}
    </motion.div>
  );
}

export default WeekView;
